import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { CSSTransition } from 'react-transition-group';
import styles from './Drawer.module.css';
import Icon from 'react-icons-kit';
import { ic_close } from 'react-icons-kit/md/ic_close';
import { ic_arrow_forward } from 'react-icons-kit/md/ic_arrow_forward';
import styled from 'styled-components';
import Button from '@ls/react-button';

const classNames = {
  enter: styles.enter,
  enterActive: styles.enterActive,
  exit: styles.exit,
  exitActive: styles.exitActive,
  exitDone: styles.exitDone,
};

const maskClassNames = {
  enter: styles.maskEnter,
  enterActive: styles.maskEnterActive,
  exit: styles.maskExit,
  exitActive: styles.maskExitActive,
};

const logoHeight = `50px`;
const Root = styled.div`
  height: 100vh;
  min-width: 250px;
  max-width: 100vw;
  position: fixed;
  top: 0;
  left: 0;
  display: flex;
  flex-direction: column;
  background: #fafafa;
  z-index: 10;
  transition: transform 0.5s cubic-bezier(0.77, 0.2, 0.05, 1);
  transform-origin: 0 0;
  box-shadow: 0 2px 2px 0 rgba(0, 0, 0, 0.14), 0 3px 1px -2px rgba(0, 0, 0, 0.2),
    0 1px 5px 0 rgba(0, 0, 0, 0.12);
`;
const TopSection = styled.div`
  background: ${({ theme }) => theme.primaryColor};
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24);
  z-index: 1;
  padding: 10px;
  position: relative;
`;

const MainSection = styled.div`
  position: relative;
  display: flex;
  flex-direction: row;
  flex: 1 1 100px;
  overflow: hidden;
`;
const Content = styled.div`
  width: 100%;
  position: relative;
  overflow: auto;
`;
const GoButtonSection = styled.div`
  align-items: center;
  display: flex;
  background: rgba(0, 0, 0, 0.05);
  padding: 0 6px;
  border-left: 1px solid #bfbfbf;
`;

const Logo = styled.img.attrs(({ theme }) => ({ src: theme.logoSm }))`
  height: ${logoHeight};
  margin: 0 10px 0 0;
`;
const Title = styled.div`
  color: white;
  font-size: 1.5em;
  cursor: default;
  user-select: none;
  max-width: 15em;
  word-break: break-word;
`;

const CloseBtn = styled.div`
  cursor: pointer;
  color: #ffffff;
`;
const Label = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const iconStyle = { marginLeft: '2px' };

const Drawer = ({
  active,
  suppressMask,
  onClickCloseButton,
  children,
  unmountOnExit,
  title,
  onClickGo,
  goButtonProps,
}) => {
  /**handle CTRL + ENTER short cut**/
  useEffect(() => {
    const eventListener = async (event) => {
      if (
        event.ctrlKey &&
        event.code === 'Enter' &&
        !event.shiftKey &&
        !event.altKey &&
        !event.repeat
      ) {
        event.preventDefault();
        if (onClickGo && !goButtonProps?.disabled) {
          onClickGo();
        }
      }
    };

    if (onClickGo) {
      window.document.body.addEventListener('keydown', eventListener);
    }
    return () => {
      window.document.body.removeEventListener('keydown', eventListener);
    };
  }, [goButtonProps?.disabled, onClickGo]);

  return (
    <>
      <CSSTransition
        in={active}
        timeout={550}
        classNames={classNames}
        unmountOnExit={unmountOnExit}
      >
        <Root>
          <TopSection>
            {onClickCloseButton && (
              <CloseBtn
                className={styles.closeBtn}
                onClick={onClickCloseButton}
              >
                <Icon icon={ic_close} size={34} />
              </CloseBtn>
            )}
            <Logo />
            <Title>{title}</Title>
          </TopSection>
          <MainSection>
            <Content>{children}</Content>
            {onClickGo && (
              <GoButtonSection left={6}>
                <Button
                  onButtonClick={onClickGo}
                  label={
                    <Label>
                      Go
                      <Icon
                        icon={ic_arrow_forward}
                        size={18}
                        style={iconStyle}
                      />
                    </Label>
                  }
                  variation={3}
                  {...goButtonProps}
                />
              </GoButtonSection>
            )}
          </MainSection>
        </Root>
      </CSSTransition>
      {!suppressMask && (
        <CSSTransition
          in={active}
          timeout={300}
          unmountOnExit
          classNames={maskClassNames}
        >
          <div className={styles.mask} />
        </CSSTransition>
      )}
    </>
  );
};

Drawer.propTypes = {
  children: PropTypes.any,
  active: PropTypes.bool.isRequired,
  onClickCloseButton: PropTypes.func,
  onClickGo: PropTypes.func,
  rootStyle: PropTypes.object,
  goButtonProps: PropTypes.object,
};

Drawer.defaultProps = {
  closeButtonSize: 34,
  onClickCloseButton: null,
  onClickGo: null,
  unmountOnExit: true,
  goButtonProps: null,
};

export default Drawer;
